import { ChamferOutline } from "@/components/chamfer";
import { Column, Row, Text } from "@/components";
import { GoalPanelPeriodToggle } from "@/components/dashboard/dashboard-stats/main-stats/GoalPanelPeriodToggle";

import {
  GOAL_PANEL_CONTAINER_CLASS,
  GOAL_PANEL_NARROW_LABEL_CLASS,
  GOAL_PANEL_NARROW_STACK_CLASS,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import { GoalPeriod } from "@/types";

const FULL_PROGRESS_PERCENT = 100;

// A count-toward-goal panel: the period's total over its target, with how far
// is left and a bar for the gap. Used by submits and revenue.
export const GoalMetricPanel = ({
  barClassName,
  borderClassName,
  goal,
  goalLabel,
  goalsPeriod,
  isSelected,
  label,
  onGoalsPeriodChange,
  panelClassName,
  remainingLabel,
  textClassName,
  value,
  valueLabel,
}: {
  barClassName: string;
  borderClassName: string;
  goal: number;
  goalLabel: string;
  goalsPeriod: GoalPeriod;
  isSelected: boolean;
  label: string;
  onGoalsPeriodChange: (period: GoalPeriod) => void;
  panelClassName: string;
  remainingLabel: string;
  textClassName: string;
  value: number;
  valueLabel: string;
}) => {
  /* Helpers */
  const progressPercent =
    goal > 0 ? Math.min(FULL_PROGRESS_PERCENT, (value / goal) * 100) : 0;

  /* Render */
  const render = () => (
    <Column
      className={cn(
        GOAL_PANEL_CONTAINER_CLASS,
        isSelected ? "lever-kpi-selected" : "lever-kpi-grid",
        "lever-chamfer-card relative h-full w-full justify-between rounded-lever-md border p-4 font-lever-suisse",
        borderClassName,
        panelClassName,
      )}
    >
      <ChamferOutline key={String(isSelected)} isSelected={isSelected} />
      <Row className="items-start justify-between gap-2">
        <Text
          className={cn(
            "font-lever-accent text-[9px] font-normal uppercase tracking-wider",
            textClassName,
          )}
        >
          {label}
        </Text>
      </Row>
      {isSelected ? (
        <GoalPanelPeriodToggle
          onPeriodChange={onGoalsPeriodChange}
          period={goalsPeriod}
        />
      ) : null}
      <Row className={cn("items-end gap-1.5", GOAL_PANEL_NARROW_STACK_CLASS)}>
        <Text
          className={cn(
            "lever-heading-h3 lever-kpi-accent whitespace-nowrap font-normal tabular-nums tracking-tight",
            textClassName,
          )}
        >
          {valueLabel}
        </Text>
        <Text
          className={cn(
            GOAL_PANEL_NARROW_LABEL_CLASS,
            "pb-1 font-lever-accent text-[10px] font-normal opacity-70",
            textClassName,
          )}
        >
          / {goalLabel}
        </Text>
      </Row>
      <Column className="gap-1.5">
        <Text
          className={cn(
            "font-lever-suisse text-[10px] font-normal",
            textClassName,
          )}
        >
          {remainingLabel} left to goal
        </Text>
        {/* The track has to read against both panel states: a tinted selected
            panel and a plain white one. */}
        <Column
          className={cn(
            "h-1 w-full overflow-hidden rounded-none",
            isSelected
              ? "bg-lever-surface-default/70"
              : "bg-lever-surface-neutral",
          )}
        >
          <Column
            className={cn("h-full rounded-none", barClassName)}
            style={{ width: `${progressPercent.toString()}%` }}
          />
        </Column>
      </Column>
    </Column>
  );

  return render();
};
